import * as DateUtils from '../lib/DateUtils.ts';
import * as TimezoneUtils from '../lib/TimezoneUtils.ts';

// Date value functions migrated from jQuery plugin
export function setDateValue(element: HTMLInputElement, date: Date | null): void {
  if (!date) {
    element.value = '';
  } else if (element.type === 'time') {
    element.value = DateUtils.forInputTime(date);
  } else if (element.type === 'date') {
    element.value = DateUtils.forInputDate(date);
  } else if (element.type === 'datetime-local') {
    let adjustedDate = date;
    if (element.getAttribute('timezone')) {
      adjustedDate = TimezoneUtils.toZone(date, element.getAttribute('timezone')!);
    }
    element.value = DateUtils.forInputDateTimeLocal(adjustedDate);
  } else {
    element.value = date.toISOString();
  }
}

export function getDateValue(element: HTMLInputElement): Date | null {
  if (!element.value) {
    return null;
  }

  if (element.type === 'date') {
    try {
      const hour = element.getAttribute('data-time');
      if (hour) {
        return new Date(element.value + 'T' + hour);
      } else {
        return new Date(element.value + 'T00:00:00Z');
      }
    } catch (e) {
      // Case of old Safari date might look like 05/19/2023
      // We do nothing and pray that the following new Date() does something good
      console.log(e);
    }
  } else if (element.type === 'time') {
    return new Date(new Date().toISOString().substring(0, 11) + element.value);
  }

  if (element.getAttribute('timezone')) {
    return TimezoneUtils.fromZone(new Date(element.value), element.getAttribute('timezone')!);
  }

  return new Date(element.value);
}

export function getLocalDateValue(node: HTMLInputElement): Date | null {
  if (!node.value) {
    return null;
  }
  if (node.getAttribute('type') === 'date') {
    try {
      const hour = node.getAttribute('data-time');
      if (hour) {
        return new Date(node.value + 'T' + hour);
      } else {
        return new Date(node.value + 'T00:00:00');
      }
    } catch (e) {
      // Case of old Safari date might look like 05/19/2023
      // We do nothing and pray that the following new Date() does something good
      console.log(e);
    }
  } else if (node.getAttribute('type') === 'time') {
    return new Date(new Date().toISOString().substring(0, 11) + node.value);
  }
  if (node.getAttribute('timezone')) {
    return TimezoneUtils.fromZone(new Date(node.value), node.getAttribute('timezone')!);
  }
  return new Date(node.value);
}

// Amount value functions migrated from jQuery plugin
export function setAmountValue(element: HTMLInputElement, amount: number): void {
  if (typeof amount !== 'number') {
    throw new Error(amount + ' is not a number');
  }

  const currencyCode = element.getAttribute('data-currency-code');
  const displayCurrency = element.getAttribute('data-currency-display') !== 'false';
  const lang = document.documentElement.lang;

  const formatter = new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: currencyCode || 'USD'
  });

  const value = displayCurrency
    ? formatter.format(amount)
    : formatter.format(amount).replace(/[^0123456798., ]/g, '');

  element.value = value;
}

export function getAmountValue(element: HTMLInputElement): number {
  const currencyCode = element.getAttribute('data-currency-code');
  const lang = document.documentElement.lang;

  const formatter = new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: currencyCode || 'USD'
  });

  const formatterPart = formatter.formatToParts(1000.5);
  const decimal = formatterPart.find(part => part.type === 'decimal')?.value;
  const isLenient = decimal !== undefined && formatterPart.filter(part => part.value === '.' || part.value === ',').length < 2;

  const verifyAmount = (amount: number): number => {
    if (isNaN(amount)) return 0;
    if (amount > 9999999.99) amount = 9999999.99;
    if (amount < 0) amount = 0;
    return amount;
  };

  const parse = (stringAmount: string): number => {
    // First cleaning
    stringAmount = stringAmount.replace(/[^0123456798.,]/g, '');

    // In French the decimal separator is , but user use . which is not a separator
    if (isLenient) {
      stringAmount = stringAmount.replaceAll(',', '.');
    } else if (decimal === ',') {
      stringAmount = stringAmount.replaceAll('.', '').replaceAll(',', '.');
    } else {
      stringAmount = stringAmount.replaceAll(',', '');
    }

    // We keep only the last dot
    while (stringAmount.indexOf('.') !== stringAmount.lastIndexOf('.')) {
      stringAmount = stringAmount.substring(0, stringAmount.indexOf('.')) +
                     stringAmount.substring(stringAmount.indexOf('.') + 1);
    }

    return verifyAmount(parseFloat(stringAmount));
  };

  return parse(element.value);
}
